"use client";
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Shield, KeyRound, RefreshCw, CheckCircle } from 'lucide-react';
import { useAuth } from './context/AuthContext'; // ✅ import useAuth

const OTP_LENGTH = 6;

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { validateOtp, forgotPassword } = useAuth(); // ✅ pull from context

const email = location.state?.email || localStorage.getItem("reset_email") || '';

  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (errors.otp) setErrors({});
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        const updated = [...otp];
        updated[index - 1] = '';
        setOtp(updated);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const updated = [...otp];
    pasted.split('').forEach((char, i) => { updated[i] = char; });
    setOtp(updated);
    const nextEmpty = updated.findIndex((v) => !v);
    const focusIndex = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty;
    inputRefs.current[focusIndex]?.focus();
  };

  const otpValue = otp.join('');
  const isComplete = otpValue.length === OTP_LENGTH;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete) {
      setErrors({ otp: `Please enter all ${OTP_LENGTH} digits` });
      return;
    }

    setIsLoading(true);
    setErrors({});

    // ✅ use context method instead of direct fetch
    const result = await validateOtp(email, otpValue);

    if (!result.success) {
      setErrors({ otp: result.message || 'Invalid OTP. Please try again.' });
      setOtp(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsVerified(true);

    setTimeout(() => {
      navigate('/dashboard/reset-password', {
        state: { email, otp: otpValue }, // ✅ pass otp instead of token (context resetPassword needs it)
      });
    }, 1800);
  };

  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    setErrors({});
    setOtp(Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();

    // ✅ use context method instead of direct fetch
    await forgotPassword(email);

    setCanResend(false);
    setResendTimer(30);
    setIsResending(false);
  };

  return (
   
    <div className="min-h-screen bg-gradient-to-br from-orange-200 via-orange-100 to-orange-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-teal-200 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-orange-300 rounded-full opacity-30 animate-float-delayed"></div>
        <div className="absolute bottom-32 left-1/4 w-40 h-40 bg-teal-100 rounded-full opacity-25 animate-float"></div>
        <div className="absolute bottom-20 right-10 w-28 h-28 bg-orange-200 rounded-full opacity-20 animate-float-delayed"></div>
      </div>

      <div className="relative w-full max-w-md z-10">
        {/* Brand */}
        <div className="text-center mb-8">
         <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl shadow-lg mb-4 transform hover:scale-105 transition-transform duration-300">
  <img
    src="https://cdn.acsdev.in/ManaPrint/manaprint_logo_tras.png"
    alt="ManaPrint Logo"
    className="w-10 h-10 object-contain"
  />
</div>
          <h1 className="text-3xl font-bold text-teal-800 mb-2">
            {isVerified ? 'Verified!' : 'Verify OTP'}
          </h1>
          <p className="text-gray-600">
            {isVerified
              ? 'Redirecting you to reset your password…'
              : email
              ? <>Code sent to <span className="font-medium text-teal-700">{email}</span></>
              : 'Enter the OTP sent to your email'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white bg-opacity-80 backdrop-blur-lg rounded-2xl shadow-2xl border border-white border-opacity-50 p-8 transform hover:scale-[1.02] transition-all duration-300">

          {/* ── SUCCESS STATE ── */}
          {isVerified ? (
            <div className="flex flex-col items-center text-center space-y-5 animate-fade-in">
              <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-teal-600" />
              </div>
              <p className="text-sm text-gray-600">OTP verified successfully. Taking you to the reset password page…</p>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-teal-700 h-full rounded-full animate-progress"></div>
              </div>
            </div>

          ) : (
            /* ── FORM STATE ── */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* OTP inputs */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <KeyRound className="w-4 h-4 text-teal-600" />
                  Enter 6-digit OTP
                </label>

                <div className="flex items-center justify-between gap-2" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      disabled={isLoading}
                      className={`
                        w-12 h-14 text-center text-xl font-bold rounded-xl border-2
                        bg-white bg-opacity-70 transition-all duration-200
                        focus:outline-none focus:scale-105
                        ${errors.otp
                          ? 'border-red-300 text-red-600 focus:ring-2 focus:ring-red-400 focus:border-red-400'
                          : digit
                          ? 'border-teal-400 text-teal-700 focus:ring-2 focus:ring-teal-400 focus:border-teal-500'
                          : 'border-gray-300 text-gray-800 focus:ring-2 focus:ring-teal-400 focus:border-teal-500'
                        }
                      `}
                    />
                  ))}
                </div>

                {errors.otp && (
                  <p className="text-red-500 text-sm animate-shake">{errors.otp}</p>
                )}
              </div>

              {/* Progress dots */}
              <div className="flex justify-center gap-2">
                {otp.map((d, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      d ? 'bg-teal-500 scale-110' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !isComplete}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-800 text-white py-3 px-6 rounded-xl font-semibold hover:from-teal-700 hover:to-teal-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verifying…
                  </>
                ) : (
                  <>
                    Verify OTP
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {/* Resend */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Didn't receive the code?</p>
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isResending}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-800 transition-colors duration-200 hover:underline disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                    {isResending ? 'Resending…' : 'Resend OTP'}
                  </button>
                ) : (
                  <p className="text-sm text-gray-400">
                    Resend in{' '}
                    <span className="font-semibold text-teal-600 tabular-nums">
                      00:{String(resendTimer).padStart(2, '0')}
                    </span>
                  </p>
                )}
              </div>

              {/* Back */}
              <Link
                to="/dashboard/forgot-password"
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-300"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Link>
            </form>
          )}
        </div>

        {/* Security notice */}
        <div className="mt-4 p-4 bg-white bg-opacity-50 backdrop-blur-sm rounded-xl border border-white border-opacity-60">
          <div className="flex items-start gap-3">
            <Shield className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600">
              <span className="font-semibold text-gray-700">OTP expires in 10 minutes — </span>
              Never share your code with anyone.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-progress { animation: progress 1.8s ease-in-out forwards; }
      `}</style>
    </div>
  );
};

export default OtpVerification;